import React, { useState } from "react";
import axios from "axios";
import emailjs from "@emailjs/browser";
import { motion } from "framer-motion";
import { MapPin, Phone, Clock, Accessibility, ArrowRight, Mail } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n/LanguageContext";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

export const ContactMap = ({ hideHeader = false }) => {
  const { t, lang } = useLang();
  const f = t("contact.form");
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      // Always save to backend (lead capture)
      await axios.post(`${API}/contact`, { ...form, locale: lang });

      // Send via EmailJS if configured
      if (EMAILJS_SERVICE_ID && EMAILJS_TEMPLATE_ID && EMAILJS_PUBLIC_KEY) {
        await emailjs.send(
          EMAILJS_SERVICE_ID,
          EMAILJS_TEMPLATE_ID,
          {
            from_name: form.name,
            from_email: form.email,
            phone: form.phone,
            subject: form.subject || "Anfrage Pick Bodenbeläge",
            message: form.message,
            locale: lang,
          },
          { publicKey: EMAILJS_PUBLIC_KEY }
        );
      }

      toast.success(f.success);
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      console.error("Contact submit failed", err);
      toast.error(f.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative bg-[#F9F8F6]" data-testid="contact-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32">
        {!hideHeader && (
          <>
            <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#8A5A44] mb-5">
              — {t("contact.eyebrow")}
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter max-w-3xl leading-[0.95] mb-14" data-testid="contact-title">
              {t("contact.title")}
            </h2>
          </>
        )}

        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Info column */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5 flex flex-col gap-8"
          >
            <InfoBlock
              icon={MapPin}
              label={t("contact.address_label")}
              value={t("contact.address")}
              testId="contact-address"
            />
            <InfoBlock icon={Phone} label={t("contact.phone_label")} value={t("contact.phone")} testId="contact-phone" link={`tel:+4920873068`} />
            <InfoBlock icon={Clock} label={t("contact.hours_label")} value={t("contact.hours")} testId="contact-hours" />
            <InfoBlock icon={Accessibility} label={t("contact.access_label")} value={t("contact.access")} testId="contact-access" />

            <a
              href="https://maps.google.com/?q=Schultenhofstraße+46,+45475+Mülheim+an+der+Ruhr"
              target="_blank"
              rel="noopener noreferrer"
              className="link-underline inline-flex items-center gap-2 text-sm mt-2 w-fit"
              data-testid="contact-directions-link"
            >
              {lang === "de" ? "Route in Google Maps öffnen" : "Open in Google Maps"}
              <ArrowRight size={16} />
            </a>
          </motion.div>

          {/* Form column */}
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            onSubmit={onSubmit}
            className="lg:col-span-7 bg-white hairline p-8 lg:p-12 flex flex-col gap-5"
            data-testid="contact-form"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <Field id="name" label={f.name} required>
                <Input id="name" required value={form.name} onChange={update("name")} className="bg-transparent border-0 border-b border-[#DCD7D1] rounded-none focus-visible:ring-0 focus-visible:border-[#1A1A1A] px-0 h-11" data-testid="contact-input-name" />
              </Field>
              <Field id="email" label={f.email} required>
                <Input id="email" type="email" required value={form.email} onChange={update("email")} className="bg-transparent border-0 border-b border-[#DCD7D1] rounded-none focus-visible:ring-0 focus-visible:border-[#1A1A1A] px-0 h-11" data-testid="contact-input-email" />
              </Field>
              <Field id="phone" label={f.phone}>
                <Input id="phone" value={form.phone} onChange={update("phone")} className="bg-transparent border-0 border-b border-[#DCD7D1] rounded-none focus-visible:ring-0 focus-visible:border-[#1A1A1A] px-0 h-11" data-testid="contact-input-phone" />
              </Field>
              <Field id="subject" label={f.subject}>
                <Input id="subject" value={form.subject} onChange={update("subject")} className="bg-transparent border-0 border-b border-[#DCD7D1] rounded-none focus-visible:ring-0 focus-visible:border-[#1A1A1A] px-0 h-11" data-testid="contact-input-subject" />
              </Field>
            </div>

            <Field id="message" label={f.message} required>
              <Textarea
                id="message"
                required
                rows={5}
                value={form.message}
                onChange={update("message")}
                className="bg-transparent border-0 border-b border-[#DCD7D1] rounded-none focus-visible:ring-0 focus-visible:border-[#1A1A1A] px-0 resize-none"
                data-testid="contact-input-message"
              />
            </Field>

            <p className="text-xs text-[#4A4A4A] leading-relaxed">{f.privacy}</p>

            <div className="flex items-center justify-between gap-4 pt-2">
              <a
                href="mailto:info@teppichpick.de"
                className="link-underline inline-flex items-center gap-2 text-sm"
                data-testid="contact-mailto-link"
              >
                <Mail size={16} /> info@teppichpick.de
              </a>
              <button
                type="submit"
                disabled={submitting}
                className="btn-primary disabled:opacity-60"
                data-testid="contact-submit-btn"
              >
                {submitting ? f.sending : f.submit}
                {!submitting && <ArrowRight size={16} />}
              </button>
            </div>
          </motion.form>
        </div>
      </div>

      {/* Embedded map */}
      <div className="w-full h-[420px] lg:h-[520px] bg-[#EFEBE6]" data-testid="contact-map">
        <iframe
          title="Pick Bodenbeläge Mülheim — Karte"
          src="https://www.google.com/maps?q=Schultenhofstraße+46,+45475+Mülheim+an+der+Ruhr&output=embed"
          loading="lazy"
          className="w-full h-full border-0 grayscale-[20%]"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </div>
    </section>
  );
};

const Field = ({ id, label, required, children }) => (
  <div className="flex flex-col gap-2">
    <Label htmlFor={id} className="text-[10px] uppercase tracking-[0.22em] text-[#4A4A4A]">
      {label}{required ? " *" : ""}
    </Label>
    {children}
  </div>
);

const InfoBlock = ({ icon: Icon, label, value, testId, link }) => {
  const Content = (
    <>
      <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.25em] text-[#8A5A44] mb-2">
        <Icon size={14} strokeWidth={1.6} />
        {label}
      </div>
      <div className="font-display text-xl lg:text-2xl font-light whitespace-pre-line leading-snug text-[#1A1A1A]">
        {value}
      </div>
    </>
  );
  if (link) {
    return (
      <a href={link} className="block group" data-testid={testId}>
        {Content}
      </a>
    );
  }
  return <div data-testid={testId}>{Content}</div>;
};
